/**
 * Work list 내보내기(PDF·Excel) 공통 — WebP 등은 canvas로 JPEG로 변환해 소비 라이브러리가 처리할 수 있게 함.
 * @react-pdf/renderer Image는 원격 WebP를 쓰지 못하는 경우가 많아 data: URL JPEG로 넘긴다.
 */

/** RIFF … WEBP 시그니처 */
export function sniffWebpMagicPrefix(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  );
}

export async function rasterBlobToJpegBlob(blob: Blob, maxPx: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    const objUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objUrl);
      try {
        const w = img.naturalWidth || 1;
        const h = img.naturalHeight || 1;
        const scale = Math.min(1, maxPx / Math.max(w, h));
        const cw = Math.max(1, Math.round(w * scale));
        const ch = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, cw, ch);
        canvas.toBlob((b) => resolve(b ?? null), 'image/jpeg', 0.88);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objUrl);
      resolve(null);
    };
    img.crossOrigin = 'anonymous';
    img.src = objUrl;
  });
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsDataURL(blob);
  });
}

/** HTTP(S) 썸네일 URI를 react-pdf가 그릴 수 있게 jpeg/png data URL 또는 원본 유지 */
export async function ensureHttpThumbUriForReactPdf(uri: string): Promise<string> {
  const trimmed = uri.trim();
  if (
    !trimmed ||
    trimmed.startsWith('data:image/jpeg') ||
    trimmed.startsWith('data:image/png') ||
    trimmed.startsWith('data:image/gif')
  ) {
    return trimmed;
  }
  if (!/^https?:\/\//i.test(trimmed)) return trimmed;

  const looksDefinitelyRasterPdfSafe =
    /\.(jpe?g|png|gif)(\?|#|$)/i.test(trimmed) && !/\.webp(\?|#|$)/i.test(trimmed);
  if (looksDefinitelyRasterPdfSafe) return trimmed;

  try {
    const res = await fetch(trimmed, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) return trimmed;
    const blob = await res.blob();
    const ct = (res.headers.get('content-type') || '').toLowerCase();

    if (ct.includes('jpeg') || ct.includes('jpg') || ct.includes('png') || ct.includes('gif')) {
      return trimmed;
    }

    const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    const looksWebp =
      ct.includes('webp') || /\.webp(\?|#|$)/i.test(trimmed) || sniffWebpMagicPrefix(head);

    if (!looksWebp) return trimmed;

    const jpegBlob = await rasterBlobToJpegBlob(blob, 320);
    if (!jpegBlob) return trimmed;
    return await blobToDataUrl(jpegBlob);
  } catch {
    return trimmed;
  }
}
